import React, { useState } from 'react';
import { Linkedin, Upload, X, FileText, CheckCircle, AlertCircle } from 'lucide-react';

const CertificationsImport = ({ onImport, existingCount = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  const handleCsvFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      return;
    }

    // Check file size (5 MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024; // 5 MB
    if (selectedFile.size > maxSize) {
      setError(`File size exceeds the 5 MB limit. Your file is ${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB. Please use a smaller file.`);
      setCsvFile(null);
      setPreviewData(null);
      return;
    }

    if (selectedFile.type !== 'text/csv' && !selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file.');
      setCsvFile(null);
      return;
    }

    // Set file and clear previous errors
    setCsvFile(selectedFile);
    setError(null);
    setLoading(true);
    setPreviewData(null);
    
    try {
      // Add a small delay to ensure UI updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const certifications = await extractCertificationsFromCSV(selectedFile);
      
      if (!certifications || certifications.length === 0) {
        setError('No certifications found in the CSV file. Please ensure the file contains certification information with headers: Name, Url, Authority, Started On, Finished On, License Number');
        setCsvFile(null);
        setPreviewData(null);
      } else {
        setPreviewData(certifications);
        setError(null);
      }
    } catch (err) {
      console.error('CSV parsing error:', err);
      let errorMessage = 'Failed to parse CSV file. ';
      if (err.message) {
        errorMessage = err.message;
      } else {
        errorMessage += 'Please ensure the file is a valid CSV file with the correct format.';
      }
      setError(errorMessage);
      setCsvFile(null);
      setPreviewData(null);
    } finally {
      setLoading(false);
    }
  };

  const extractCertificationsFromCSV = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const certifications = [];
          
          // Split by lines
          const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
          
          if (lines.length < 2) {
            reject(new Error('CSV file appears to be empty or has no data rows.'));
            return;
          }
          
          // Parse header
          const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          
          // Find column indices (case-insensitive)
          const nameIndex = header.findIndex(h => h.toLowerCase() === 'name');
          const urlIndex = header.findIndex(h => h.toLowerCase() === 'url');
          const authorityIndex = header.findIndex(h => h.toLowerCase() === 'authority');
          const startedOnIndex = header.findIndex(h => h.toLowerCase().includes('started'));
          const finishedOnIndex = header.findIndex(h => h.toLowerCase().includes('finished'));
          const licenseNumberIndex = header.findIndex(h => h.toLowerCase().includes('license') && h.toLowerCase().includes('number'));
          
          if (nameIndex === -1) {
            reject(new Error('CSV file must contain "Name" column.'));
            return;
          }
          
          // Helper function to parse CSV line with quoted fields
          const parseCSVLine = (line) => {
            const result = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              const nextChar = line[i + 1];
              
              if (char === '"' && inQuotes && nextChar === '"') {
                current += '"';
                i++; // Skip next quote
              } else if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            
            result.push(current.trim());
            return result.map(field => field.replace(/^"|"$/g, ''));
          };
          
          // Helper to parse date like "Jun 2022" or "2022"
          const parseDate = (dateStr) => {
            if (!dateStr || dateStr.trim() === '') {
              return { month: '', year: '' };
            }
            
            const str = dateStr.trim();
            
            // Parse "Jun 2022" or "June 2022" format
            const monthYearMatch = str.match(/(Jan|January|Feb|February|Mar|March|Apr|April|May|Jun|June|Jul|July|Aug|August|Sep|September|Oct|October|Nov|November|Dec|December)\s+(\d{4})/i);
            if (monthYearMatch) {
              // Map to full month names to match ResumeEditor dropdown values
              const monthMap = {
                'jan': 'January', 'january': 'January',
                'feb': 'February', 'february': 'February',
                'mar': 'March', 'march': 'March',
                'apr': 'April', 'april': 'April',
                'may': 'May',
                'jun': 'June', 'june': 'June',
                'jul': 'July', 'july': 'July',
                'aug': 'August', 'august': 'August',
                'sep': 'September', 'september': 'September',
                'oct': 'October', 'october': 'October',
                'nov': 'November', 'november': 'November',
                'dec': 'December', 'december': 'December'
              };
              const month = monthMap[monthYearMatch[1].toLowerCase()] || monthYearMatch[1];
              return { month, year: monthYearMatch[2] };
            }
            
            // Parse just year "2022"
            const yearMatch = str.match(/(\d{4})/);
            if (yearMatch) {
              return { month: '', year: yearMatch[1] };
            }
            
            return { month: '', year: '' };
          };
          
          // Parse data rows
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i];
            if (!line.trim()) continue;
            
            const values = parseCSVLine(line);
            
            if (values.length > nameIndex) {
              const name = values[nameIndex]?.trim() || '';
              const url = urlIndex >= 0 ? values[urlIndex]?.trim() || '' : '';
              const authority = authorityIndex >= 0 ? values[authorityIndex]?.trim() || '' : '';
              const startedOn = startedOnIndex >= 0 ? values[startedOnIndex]?.trim() || '' : '';
              const finishedOn = finishedOnIndex >= 0 ? values[finishedOnIndex]?.trim() || '' : '';
              const licenseNumber = licenseNumberIndex >= 0 ? values[licenseNumberIndex]?.trim() || '' : '';
              
              // Started On → Start Date
              const startDate = parseDate(startedOn);
              
              // Finished On → Issue Date
              const issueDate = parseDate(finishedOn);
              
              if (name) {
                certifications.push({
                  name,
                  issuer: authority,
                  start_month: startDate.month,
                  start_year: startDate.year,
                  issue_month: issueDate.month,
                  issue_year: issueDate.year,
                  expiration_month: '',
                  expiration_year: '',
                  credential_id: licenseNumber,
                  credential_url: url,
                  certification_logo: '',
                  skills: []
                });
              }
            }
          }
          
          if (certifications.length === 0) {
            reject(new Error('No valid certification entries found in CSV file.'));
            return;
          }
          
          resolve(certifications);
        } catch (err) {
          reject(new Error(`Failed to parse CSV file: ${err.message}`));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read CSV file'));
      };
      
      reader.readAsText(file);
    });
  };

  const handleImport = () => {
    if (previewData) {
      setLoading(true);
      try {
        onImport(previewData);
        setSuccess(`Successfully imported ${previewData.length} certification${previewData.length > 1 ? 's' : ''}.`);
        setTimeout(() => {
          setIsOpen(false);
          setCsvFile(null);
          setPreviewData(null);
          setSuccess(null);
          setError(null);
        }, 2000);
      } catch (err) {
        setError('Failed to import certifications. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setCsvFile(null);
    setPreviewData(null);
    setError(null);
    setSuccess(null);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="btn btn-outline w-full inline-flex items-center justify-center space-x-2 mb-4"
        type="button"
      >
        <Linkedin className="w-5 h-5" />
        <span>Import Entire License or Certification from LinkedIn</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Linkedin className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Import License or Certification from LinkedIn</h2>
                </div>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600"
                  type="button"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* CSV Upload Instructions */}
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    How to Export Your LinkedIn Data
                  </h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
                    <li>Go to <strong>LinkedIn Settings & Privacy</strong></li>
                    <li>Click <strong>"Data Privacy"</strong> → <strong>"Get a copy of your data"</strong></li>
                    <li>Select <strong>"Download larger data archive, including connections, verifications, contacts, account history, and information we infer about you based on your profile and activity."</strong></li>
                    <li>Click <strong>"Request archive"</strong></li>
                    <li>Download the <strong>Complete_LinkedInDataExport</strong> file when ready (usually takes a few minutes)</li>
                    <li>Upload the <strong>Certifications.csv</strong> file here</li>
                  </ol>
                  <p className="text-xs text-blue-700 mt-3">
                    <strong>Note:</strong> Maximum file size is 5 MB.
                  </p>
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleCsvFileSelect}
                      className="hidden"
                      disabled={loading}
                    />
                    <span className="btn btn-primary inline-flex items-center space-x-2">
                      <Upload className="w-5 h-5" />
                      <span>{loading ? 'Processing CSV...' : 'Choose CSV File'}</span>
                    </span>
                  </label>
                  {csvFile && (
                    <p className="mt-4 text-sm text-gray-600">
                      Selected: <span className="font-semibold">{csvFile.name}</span>
                      {loading && (
                        <span className="ml-2 text-primary-600">(Extracting certifications...)</span>
                      )}
                    </p>
                  )}
                </div>

                {previewData && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-green-900">
                        Found {previewData.length} certification{previewData.length > 1 ? 's' : ''}
                      </h3>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {previewData.map((cert, index) => (
                        <div key={index} className="bg-white rounded p-2 text-sm">
                          <div className="font-semibold text-gray-900">{cert.name}</div>
                          <div className="text-gray-600">{cert.issuer}</div>
                          <div className="text-gray-500 text-xs">
                            {cert.start_month && cert.start_year && `Started: ${cert.start_month} ${cert.start_year}`}
                            {cert.start_month && cert.start_year && cert.issue_month && cert.issue_year && ' • '}
                            {cert.issue_month && cert.issue_year && `Issued: ${cert.issue_month} ${cert.issue_year}`}
                          </div>
                          {cert.credential_id && (
                            <div className="text-gray-500 text-xs">
                              ID: {cert.credential_id}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-900 mb-1">Error</h3>
                    <p className="text-sm text-red-800 whitespace-pre-wrap">{error}</p>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-900 mb-1">Success</h3>
                    <p className="text-sm text-green-800">{success}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={handleClose}
                  className="btn btn-outline"
                  type="button"
                  disabled={loading}
                >
                  Cancel
                </button>
                {previewData && (
                  <button
                    onClick={handleImport}
                    className="btn btn-primary"
                    disabled={!previewData || loading}
                    type="button"
                  >
                    {loading ? 'Importing...' : `Import ${previewData?.length || 0} Certification${previewData?.length !== 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CertificationsImport;
